<template>
  <form autocomplete="off" class="px-4 py-3">
    <div v-if="doesUserHaveSession">
      <ModalWithSlot
        title="Is this you?"
        :showModal="showModal"
        modalSize="sm"
        @close:modal="$emit('close-session-modal')"
      >
        <div>
          <div class="d-flex align-items-center">
            <SfeAvatar
              class="mr-3"
              size="sm"
              :initials="
                $helpers.createInitials(
                  sessionUserDetails.user?.first_name,
                  sessionUserDetails.user?.last_name,
                )
              "
              :alt="
                $helpers.createInitials(
                  sessionUserDetails.user?.first_name,
                  sessionUserDetails.user?.last_name,
                )
              "
              status="success"
              :key="sessionUserDetails.user?.picture"
              :src="sessionUserDetails.user?.picture || ''"
            />
            <div class="ml-1">
              <p class="mb-0 text-black-1 fw-400 fs-16">
                {{ sessionUserDetails.user?.first_name }}
                {{ sessionUserDetails.user?.last_name }}
              </p>
              <p class="text-muted mb-0 fw-400 fs-16">{{ maskEmail }}</p>
            </div>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-5">
            <div class="button-container">
              <SfeButton
                :disabled="isLoading"
                @click.prevent="handleAdminLogin('withSession')"
                type="submit"
                id="admin_login_btn"
                class="px-3 fs-14 login-form__btn btn btn-primary-light-blue border-radius-8 d-flex align-items-center justify-content-center w-100"
              >
                Continue as {{ sessionUserDetails.user?.first_name }}
                <SfeLoader
                  v-if="isLoading"
                  class="ml-1"
                  size="sm"
                  color="white"
                />
              </SfeButton>
            </div>
            <div class="text-right">
              <SfeButton
                variant="outline"
                @click.prevent="$emit('log-in-as-another-user')"
                class="btn btn-outline-primary border-radius-8 fw-400 fs-14 border-line-grey-1"
              >
                Login as a different user
              </SfeButton>
            </div>
          </div>
        </div>
      </ModalWithSlot>
    </div>
    <div>
      <div
        :class="[
          company.logo_placement === 'center' ? 'text-center' : 'text-left',
        ]"
      >
        <h1 class="text-black-1 fs-24 fw-500 mb-1">
          Login to your admin portal
        </h1>
        <p class="text-light-grey-1 fs-15">
          Access admin dashboard with your work email
        </p>
      </div>

      <div
        v-if="proceedWithMagicLink && magicLinkRequestSuccess && form.email"
        class="mb-4 p-3 bg-grey-light-1 border-radius-8"
      >
        <p class="mb-0">
          If <span class="fw-500">{{ form.email }}</span> has an account, you
          will receive an email with an access link to the platform.
        </p>
      </div>

      <!-- Input fields begin  -->
      <div class="form-group mb-3 mt-3">
        <SfeLabel for="admin_email_input" class="mb-1"> Email </SfeLabel>
        <b-form-input
          id="admin_email_input"
          placeholder="example@gmail.com"
          type="email"
          :class="{
            'form-control border-radius-8 py-4 login-form__input': true,
            'disable-bg-light-grey-1': jobTeamInviteUse,
          }"
          v-model="form.email"
        ></b-form-input>
        <ShrInputError :error="errors.email" />
      </div>
      <div v-if="!proceedWithMagicLink" class="form-group mb-3">
        <SfeLabel class="mb-1"> Password </SfeLabel>
        <PasswordInput autocomplete="off" v-model="form.password" />
        <div class="mb-3 mt-2 fs-15">
          <div v-if="password_has_exired">
            <p class="mb-0 text-dark-grey-1 fw-400">
              Your password has expired.
              <span
                ><a class="text-primary fw-500" :href="userToken"
                  >Click here</a
                ></span
              >
              to reset your password
            </p>
          </div>
          <div v-else class="text-right">
            <a
              class="text-primary-blue fs-15 fw-400"
              id="admin_forgot_password"
              :href="
                jobTeamInviteUse
                  ? 'advanced#/admin/forgot-password'
                  : 'h/advanced#/admin/forgot-password'
              "
              >Reset password</a
            >
          </div>
        </div>
        <ShrInputError :error="errors.password" />
      </div>

      <!-- Input fields end -->

      <!-- Button container starts -->

      <div class="button-container mb-3">
        <SfeButton
          v-if="proceedWithMagicLink"
          @click.prevent="requestMagicLink"
          :disabled="!form.email || isRequesting"
          type="submit"
          id="admin_login_btn"
          class="login-form__btn btn fs-18 fw-400 text-center btn-primary-light-blue border-radius-8 py-2 d-flex align-items-center justify-content-center w-100"
        >
          <div class="d-flex align-items-center">
            <span> Send Access Link </span>
            <span class="material-icons ml-2 mt-1"> arrow_right_alt </span>
          </div>
          <SfeLoader v-if="isRequesting" class="ml-1" size="sm" color="white" />
        </SfeButton>
        <SfeButton
          v-else
          @click.prevent="handleAdminLogin('withoutSession')"
          :disabled="buttonIsDisabled"
          type="submit"
          id="admin_login_btn"
          class="login-form__btn btn fs-18 fw-400 text-center btn-primary-light-blue border-radius-8 py-2 d-flex align-items-center justify-content-center w-100"
        >
          Login
          <SfeLoader v-if="isLoading" class="ml-1" size="sm" color="white" />
        </SfeButton>
      </div>
      <div>
        <SfeDivider
          ><span
            class="mb-0 fs-13 text-primary cursor-pointer"
            @click.prevent="switchAuthMethod"
            >{{
              proceedWithMagicLink
                ? "Or continue with password"
                : "Or continue with magic link"
            }}</span
          ></SfeDivider
        >
      </div>
      <div class="mt-3">
        <ClientPrivacyPolicy />
      </div>
      <!-- Button container ends -->
    </div>
  </form>
</template>

<script>
import AdminModel from "../../../models/AdminModel";
import PasswordInput from "../../../components/auth/PasswordInput";
import ClientPrivacyPolicy from "../ClientPrivacyPolicy.vue";
import Auth from "../../../mixins/auth";

export default {
  name: "AdminLoginComponent",
  components: {
    PasswordInput,
    ClientPrivacyPolicy,
  },
  data() {
    return {
      currentUrl: "",
      showModal: true,
      isRequesting: false,
      magicLinkRequestSuccess: false,
      proceedWithMagicLink: true,
      doesUserHaveSession: false,
      password_has_exired: false,
      userToken: "",
      form: {
        email: "",
        password: "",
      },
      errors: {},
      isLoading: false,
    };
  },
  props: {
    userHasSession: {
      type: Boolean,
      default: false,
    },
    sessionUserDetails: {
      type: Object,
      default: () => ({}),
    },
    logoLink: {
      type: String,
      required: false,
    },
    userEmail: {
      type: String,
      default: "",
    },
    redirectQuery: {
      type: String,
      default: "",
    },
    redirectJobId: {
      type: Number,
      default: null,
    },
    company: {
      type: Object,
      default: () => ({}),
    },
    jobTeamInviteUse: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [AdminModel, Auth],
  mounted() {
    if (
      window.location.pathname !== "/" ||
      window.location.search ||
      window.location.hash
    ) {
      this.currentUrl = window.location.hash;
    }
  },
  methods: {
    switchAuthMethod() {
      this.proceedWithMagicLink = !this.proceedWithMagicLink;
      this.magicLinkRequestSuccess = false;
    },
    requestMagicLink() {
      this.isRequesting = true;
      const payload = {
        email: this.form.email,
        user_type: "admin",
      };
      this.AdminModel.requestMagicLink(payload)
        .then((r) => {
          this.$sfeToast.toast("success", "Success", r.message);
          this.magicLinkRequestSuccess = true;
        })
        .catch((e) => {
          this.$sfeToast.toast("error", "Error", e.message);
        })
        .finally(() => {
          this.isRequesting = false;
        });
    },
    getQueryParams(url) {
      const paramArr = url.slice(url.indexOf("?") + 1).split("&");
      const params = {};
      paramArr.map((param) => {
        const [key, val] = param.split("=");
        params[key] = decodeURIComponent(val);
      });
      return params;
    },
    makeLoginRequest({ requestType, adminLoginType, payload, data }) {
      return this.AdminModel[requestType](
        adminLoginType === "withSession" ? payload : data,
      )
        .then((r) => {
          this.$sfeToast.toast("success", "Success", r.message);
          const token = r.data;
          return token;
        })
        .catch((err) => {
          this.isLoading = false;
          if(err.status === 423) {
            this.$sfeToast.toast("error", "Error", err.message);
          }
          if (err.data && err.status === 424) {
            (this.password_has_exired = true),
              (this.userToken = err.data.user_token);
          }

          if (err.errors) {
            this.errors = { ...err.errors };
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleAdminLogin(adminLoginType) {
      this.isLoading = true;
      const data = {
        ...this.form,
        password: this.$encryptForBackend(this.form.password),
      };
      const payload = {
        email: this.sessionUserDetails.user?.email,
        hrms_company: this.sessionUserDetails.user?.hrms_company,
        token: this.sessionUserDetails.note,
      };
      const url = this.getQueryParams(document.URL);
      const requestType =
        adminLoginType === "withSession"
          ? "loginAdminWithActiveSession"
          : "login";
      this.makeLoginRequest({
        requestType,
        adminLoginType,
        payload,
        data,
      }).then((token) => {
        if (token) {
          this.AuthMixin().extractPermissionsAndSetLoggedInUserFromAdminToken({
            token,
            useStore: true,
          });
          const intendedRedirectPath = this.redirectPath;
          location.href = url.redirect ? url.redirect : intendedRedirectPath;
        }
      });
    },
  },
  computed: {
    maskEmail() {
      const [username, domain] =
        this.sessionUserDetails?.user?.email.split("@");
      const maskedUsername =
        username.slice(0, 1) +
        "*".repeat(username.length - 2) +
        username.slice(-1);
      return maskedUsername + "@" + domain;
    },
    redirectPath() {
      if (this.currentUrl && !this.redirectQuery) {
        return `${location.origin}/admin/advanced${this.currentUrl}`;
      } else if (this.redirectQuery === "ats") {
        return `${location.origin}/admin/advanced#/jobs/${this.redirectJobId}/applications/ALL`;
      } else {
        return "/admin/advanced#/dashboard?login=true";
      }
    },
    buttonIsDisabled() {
      return Object.values(this.form).some((parameter) => !parameter);
    },
  },
  watch: {
    jobTeamInviteUse: {
      immediate: true,
      handler(newValue) {
        this.form.email = this.userEmail;
      },
    },

    userHasSession: {
      immediate: true,
      handler(newValue) {
        this.doesUserHaveSession = newValue;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.login-form {
  &__img-xs {
    display: none;
  }
}

::v-deep .sfe-divider-content {
  width: 140% !important;
}
</style>
